import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, ArrowUpRight, ArrowDownRight, Receipt, Coins, Landmark, CreditCard, Plus, Download, Lock, Zap, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

const METHODS = [
    { value: "cash", label: "Cash", icon: Coins },
    { value: "upi", label: "UPI", icon: Zap },
    { value: "bank", label: "Bank Transfer", icon: Landmark },
    { value: "card", label: "Card", icon: CreditCard },
    { value: "other", label: "Other", icon: Wallet },
];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

// ------------------- KPI Card -------------------
function KpiCard({ label, value, hint, icon: Icon, tone = "default", testId }) {
    const tones = {
        default: "text-zinc-950",
        good: "text-emerald-700",
        warn: "text-amber-700",
        bad: "text-red-700",
    };
    return (
        <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testId}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">{label}</span>
                {Icon && <Icon className="h-3.5 w-3.5 text-zinc-400" strokeWidth={1.5} />}
            </div>
            <div className={`mt-2 text-2xl font-display tracking-tight ${tones[tone]}`} data-testid={`${testId}-value`}>{value}</div>
            {hint && <div className="mt-0.5 text-xs text-zinc-500">{hint}</div>}
        </div>
    );
}

// ------------------- Cashflow Sparkline -------------------
function CashflowSparkline({ series }) {
    if (!series?.length) return null;
    const w = 640, h = 100, pad = 4;
    const nets = series.map((s) => s.net);
    const min = Math.min(0, ...nets);
    const max = Math.max(0, ...nets);
    const range = Math.max(max - min, 1);
    const step = (w - pad * 2) / (series.length - 1 || 1);
    const y = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
    const path = series.map((s, i) => `${i === 0 ? "M" : "L"} ${pad + i * step} ${y(s.net)}`).join(" ");
    const zeroY = y(0);
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" data-testid="finance-cashflow-svg">
            <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="#e4e4e7" strokeDasharray="2 3" />
            <path d={path} fill="none" stroke="#18181b" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}

// ------------------- Expense Dialog -------------------
function ExpenseDialog({ open, onOpenChange, categories, onCreated }) {
    const [form, setForm] = useState({ date: today(), category: "", amount: "", payment_method: "cash", vendor: "", notes: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setForm({ date: today(), category: "", amount: "", payment_method: "cash", vendor: "", notes: "" });
    }, [open]);

    const save = async () => {
        if (!form.category || !form.amount) return;
        setSaving(true);
        try {
            await api.post("/finance/expenses", { ...form, amount: Number(form.amount) });
            toast.success("Expense recorded");
            onOpenChange(false);
            onCreated?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="expense-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-lg tracking-tight">Record expense</DialogTitle>
                    <DialogDescription>Log a business expense against today or a past date.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Date</Label>
                            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" data-testid="expense-date" />
                        </div>
                        <div>
                            <Label className="text-xs">Amount</Label>
                            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9" data-testid="expense-amount" placeholder="0.00" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Category</Label>
                        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                            <SelectTrigger className="h-9" data-testid="expense-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                            <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Payment method</Label>
                        <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                            <SelectTrigger className="h-9" data-testid="expense-method"><SelectValue /></SelectTrigger>
                            <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Vendor (optional)</Label>
                        <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="h-9" data-testid="expense-vendor" />
                    </div>
                    <div>
                        <Label className="text-xs">Notes</Label>
                        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="expense-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="expense-cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving || !form.category || !form.amount} data-testid="expense-save">
                        {saving ? "Saving…" : "Save expense"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ------------------- Payment Dialog -------------------
function PaymentDialog({ open, onOpenChange, onCreated }) {
    const [invoices, setInvoices] = useState([]);
    const [form, setForm] = useState({ invoice_id: "", amount: "", method: "cash", reference: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ invoice_id: "", amount: "", method: "cash", reference: "" });
            api.get("/invoices").then(({ data }) => {
                setInvoices((data.items || []).filter((i) => ["sent", "partial", "draft"].includes(i.status)));
            });
        }
    }, [open]);

    const selected = invoices.find((i) => i.id === form.invoice_id);
    const outstanding = selected ? Number(selected.grand_total) - Number(selected.amount_paid || 0) : null;

    const save = async () => {
        if (!form.amount) return;
        setSaving(true);
        try {
            if (form.invoice_id) {
                await api.post(`/invoices/${form.invoice_id}/mark-paid`, {
                    amount: Number(form.amount), method: form.method, reference: form.reference,
                });
            } else {
                await api.post("/finance/payments", {
                    amount: Number(form.amount), method: form.method, reference: form.reference,
                });
            }
            toast.success("Payment recorded");
            onOpenChange(false);
            onCreated?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="payment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-lg tracking-tight">Record payment</DialogTitle>
                    <DialogDescription>Log cash received — link to an invoice or record as standalone.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div>
                        <Label className="text-xs">Invoice (optional)</Label>
                        <Select value={form.invoice_id || "_none"} onValueChange={(v) => setForm({ ...form, invoice_id: v === "_none" ? "" : v })}>
                            <SelectTrigger className="h-9" data-testid="payment-invoice">
                                <SelectValue placeholder="Standalone cash-in" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="_none">Standalone cash-in</SelectItem>
                                {invoices.map((i) => (
                                    <SelectItem key={i.id} value={i.id}>
                                        {i.number} · {i.customer_name} · {inr(Number(i.grand_total) - Number(i.amount_paid || 0))} due
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {outstanding != null && (
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700" data-testid="payment-outstanding">
                            Outstanding on this invoice: <span className="font-mono font-semibold">{inr(outstanding)}</span>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Amount</Label>
                            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9" data-testid="payment-amount" placeholder="0.00" />
                        </div>
                        <div>
                            <Label className="text-xs">Method</Label>
                            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                                <SelectTrigger className="h-9" data-testid="payment-method"><SelectValue /></SelectTrigger>
                                <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Reference (txn id, cheque no.)</Label>
                        <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="h-9" data-testid="payment-reference" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="payment-cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving || !form.amount} data-testid="payment-save">
                        {saving ? "Saving…" : "Record payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ------------------- Main -------------------
export default function FinanceHub() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedDate, setSelectedDate] = useState(today());
    const [dashboard, setDashboard] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [expCategories, setExpCategories] = useState([]);
    const [gst, setGst] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expenseOpen, setExpenseOpen] = useState(false);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [closing, setClosing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, e, g] = await Promise.all([
                api.get(`/finance/dashboard?date=${selectedDate}`),
                api.get(`/finance/expenses?from_date=${selectedDate}&to_date=${selectedDate}`),
                api.get("/finance/gst-summary"),
            ]);
            setDashboard(d.data);
            setExpenses(e.data.items || []);
            setExpCategories(e.data.categories || []);
            setGst(g.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [selectedDate]);

    useEffect(() => { load(); }, [load]);

    // Deep-link: /finance-hub?action=record-expense auto-opens the expense dialog
    useEffect(() => {
        const action = searchParams.get("action");
        if (action === "record-expense") {
            setExpenseOpen(true);
            searchParams.delete("action");
            setSearchParams(searchParams, { replace: true });
        } else if (action === "record-payment") {
            setPaymentOpen(true);
            searchParams.delete("action");
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const closeDay = async () => {
        setClosing(true);
        try {
            await api.post("/finance/close-day", { date: selectedDate });
            toast.success(`Day ${selectedDate} closed. Snapshot saved.`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setClosing(false); }
    };

    const exportSummary = () => {
        if (!dashboard) return;
        const rows = [
            ["Metric", "Value"],
            ["Date", dashboard.date],
            ["Revenue Today (cash basis)", dashboard.cash_basis.revenue_today],
            ["Cash Sales", dashboard.cash_basis.by_method.cash],
            ["UPI Sales", dashboard.cash_basis.by_method.upi],
            ["Bank Transfers", dashboard.cash_basis.by_method.bank],
            ["Card Sales", dashboard.cash_basis.by_method.card],
            ["Expenses Today", dashboard.cash_basis.expenses_today],
            ["Supplier Payouts Today", dashboard.cash_basis.supplier_payouts_today],
            ["Estimated Daily Profit", dashboard.cash_basis.estimated_profit_today],
            ["Gross Sales Today (accrual)", dashboard.accrual_reference.gross_sales_today],
            ["Outstanding Receivables", dashboard.outstanding.receivables],
            ["Outstanding Payables", dashboard.outstanding.payables],
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `yugos-finance-${selectedDate}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    const deleteExpense = async (id) => {
        try {
            await api.delete(`/finance/expenses/${id}`);
            toast.success("Expense removed");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const cb = dashboard?.cash_basis;
    const out = dashboard?.outstanding;
    const acc = dashboard?.accrual_reference;

    return (
        <div data-testid="page-finance-hub">
            <PageHeader
                title="Finance Hub"
                description="Today's cash reality, receivables, payables, and daily reconciliation — the financial nervous system of the business."
                actions={
                    <div className="flex items-center gap-2">
                        <Input
                            type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                            max={today()} className="h-8 w-40 text-xs" data-testid="finance-date-picker"
                        />
                        <Button size="sm" variant="outline" onClick={() => setExpenseOpen(true)} className="h-8 gap-1.5" data-testid="finance-record-expense">
                            <Receipt className="h-3.5 w-3.5" /> Record Expense
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setPaymentOpen(true)} className="h-8 gap-1.5" data-testid="finance-record-payment">
                            <ArrowUpRight className="h-3.5 w-3.5" /> Record Payment
                        </Button>
                        <Button size="sm" variant="outline" onClick={exportSummary} className="h-8 gap-1.5" data-testid="finance-export">
                            <Download className="h-3.5 w-3.5" /> Export
                        </Button>
                        <Button size="sm" onClick={closeDay} disabled={closing || dashboard?.is_closed} className="h-8 gap-1.5" data-testid="finance-close-day">
                            <Lock className="h-3.5 w-3.5" /> {dashboard?.is_closed ? "Day Closed" : closing ? "Closing…" : "Close Day"}
                        </Button>
                    </div>
                }
            />

            {loading || !dashboard ? (
                <div className="p-8 text-sm text-zinc-500">Loading finance snapshot…</div>
            ) : (
                <div className="p-8 max-w-[1400px] space-y-6">
                    {/* Row 1 — cash basis */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard testId="kpi-revenue-today" label="Revenue Today" value={inr(cb.revenue_today)} hint="Cash received (all methods)" icon={ArrowUpRight} tone={cb.revenue_today > 0 ? "good" : "default"} />
                        <KpiCard testId="kpi-cash-sales" label="Cash Sales" value={inr(cb.by_method.cash)} hint="Cash payments today" icon={Coins} />
                        <KpiCard testId="kpi-upi-sales" label="UPI Sales" value={inr(cb.by_method.upi)} hint="UPI payments today" icon={Zap} />
                        <KpiCard testId="kpi-expenses-today" label="Expenses" value={inr(cb.expenses_today)} hint="Recorded today" icon={ArrowDownRight} tone={cb.expenses_today > 0 ? "warn" : "default"} />
                    </div>
                    {/* Row 2 — profit + accrual */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard testId="kpi-gross-sales" label="Gross Sales (Accrual)" value={inr(acc.gross_sales_today)} hint={`${acc.invoices_sent_today} invoices sent today`} icon={Receipt} />
                        <KpiCard testId="kpi-receivables" label="Outstanding Receivables" value={inr(out.receivables)} hint="Money owed to you" tone={out.receivables > 0 ? "warn" : "good"} icon={TrendingUp} />
                        <KpiCard testId="kpi-payables" label="Outstanding Payables" value={inr(out.payables)} hint="Money you owe suppliers" tone={out.payables > 0 ? "warn" : "good"} icon={TrendingDown} />
                        <KpiCard testId="kpi-daily-profit" label="Est. Daily Profit" value={inr(cb.estimated_profit_today)} hint="Cash − Expenses − COGS" tone={cb.estimated_profit_today >= 0 ? "good" : "bad"} icon={Wallet} />
                    </div>

                    {/* Tabs */}
                    <Tabs defaultValue="overview">
                        <TabsList data-testid="finance-tabs">
                            <TabsTrigger value="overview" data-testid="finance-tab-overview">Cash Flow</TabsTrigger>
                            <TabsTrigger value="reconciliation" data-testid="finance-tab-reconciliation">Daily Reconciliation</TabsTrigger>
                            <TabsTrigger value="expenses" data-testid="finance-tab-expenses">Expenses</TabsTrigger>
                            <TabsTrigger value="gst" data-testid="finance-tab-gst">GST Summary</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="mt-4 space-y-4">
                            <div className="rounded-lg border border-zinc-200 bg-white p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">Cash Flow · Last 30 days</div>
                                        <div className="mt-1 text-sm text-zinc-600">Daily net (inflow − outflow)</div>
                                    </div>
                                </div>
                                <CashflowSparkline series={dashboard.cash_flow_30d || []} />
                                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-zinc-100">
                                    <div>
                                        <div className="text-[10px] font-medium uppercase tracking-widest text-emerald-700">Inflow (30d)</div>
                                        <div className="font-mono text-lg mt-1">{inr(dashboard.cash_flow_30d.reduce((s, x) => s + x.inflow, 0))}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-medium uppercase tracking-widest text-red-700">Outflow (30d)</div>
                                        <div className="font-mono text-lg mt-1">{inr(dashboard.cash_flow_30d.reduce((s, x) => s + x.outflow, 0))}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-700">Net (30d)</div>
                                        <div className="font-mono text-lg mt-1">{inr(dashboard.cash_flow_30d.reduce((s, x) => s + x.net, 0))}</div>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="reconciliation" className="mt-4">
                            <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="finance-reconciliation">
                                <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500 mb-3">Daily Reconciliation · {dashboard.date}</div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    {METHODS.map((m) => (
                                        <div key={m.value} className="rounded-md border border-zinc-200 bg-zinc-50/40 p-3">
                                            <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-widest text-zinc-500">
                                                <m.icon className="h-3 w-3" strokeWidth={1.5} /> {m.label}
                                            </div>
                                            <div className="mt-1 font-mono text-lg">{inr(cb.by_method[m.value])}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div><span className="text-zinc-500 mr-2">Cash in:</span><span className="font-mono">{inr(cb.revenue_today)}</span></div>
                                    <div><span className="text-zinc-500 mr-2">Expenses:</span><span className="font-mono text-red-700">−{inr(cb.expenses_today)}</span></div>
                                    <div><span className="text-zinc-500 mr-2">Supplier payouts:</span><span className="font-mono text-red-700">−{inr(cb.supplier_payouts_today)}</span></div>
                                    <div><span className="text-zinc-500 mr-2">COGS:</span><span className="font-mono text-red-700">−{inr(cb.cogs_today)}</span></div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-zinc-100 text-sm font-medium">
                                    <span className="text-zinc-500 mr-2">Estimated profit:</span>
                                    <span className={`font-mono text-base ${cb.estimated_profit_today >= 0 ? "text-emerald-700" : "text-red-700"}`}>{inr(cb.estimated_profit_today)}</span>
                                </div>
                                {dashboard.is_closed && (
                                    <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                                        <Lock className="h-3.5 w-3.5" strokeWidth={1.5} /> Day closed on {dashboard.closure?.closed_at?.slice(0, 16).replace("T", " ")}
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="expenses" className="mt-4">
                            <ExpenseList
                                items={expenses}
                                onDelete={deleteExpense}
                                onAdd={() => setExpenseOpen(true)}
                            />
                        </TabsContent>

                        <TabsContent value="gst" className="mt-4">
                            <GstSummary gst={gst} />
                        </TabsContent>
                    </Tabs>
                </div>
            )}

            <ExpenseDialog open={expenseOpen} onOpenChange={setExpenseOpen} categories={expCategories} onCreated={load} />
            <PaymentDialog open={paymentOpen} onOpenChange={setPaymentOpen} onCreated={load} />
        </div>
    );
}

function ExpenseList({ items, onDelete, onAdd }) {
    const total = useMemo(() => items.reduce((s, e) => s + Number(e.amount || 0), 0), [items]);
    return (
        <div className="rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="finance-expense-list">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
                <div>
                    <div className="text-sm font-medium">Expenses on this day</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{items.length} entries · Total <span className="font-mono">{inr(total)}</span></div>
                </div>
                <Button size="sm" variant="outline" onClick={onAdd} className="h-8" data-testid="expense-add-btn">
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add
                </Button>
            </div>
            {items.length === 0 ? (
                <div className="py-10 text-center text-sm text-zinc-500">No expenses recorded on this day.</div>
            ) : (
                <Table>
                    <TableHeader><TableRow className="bg-zinc-50/40">
                        {["Category", "Amount", "Method", "Vendor", "Notes", ""].map((h) => <TableHead key={h} className="text-[10px] uppercase tracking-widest text-zinc-500">{h}</TableHead>)}
                    </TableRow></TableHeader>
                    <TableBody>
                        {items.map((e) => (
                            <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                                <TableCell><span className="text-sm">{e.category}</span></TableCell>
                                <TableCell className="font-mono text-sm">{inr(e.amount)}</TableCell>
                                <TableCell><span className="text-xs uppercase tracking-wider text-zinc-500">{e.payment_method}</span></TableCell>
                                <TableCell className="text-xs text-zinc-600">{e.vendor || "—"}</TableCell>
                                <TableCell className="text-xs text-zinc-500 max-w-[200px] truncate">{e.notes || "—"}</TableCell>
                                <TableCell className="text-right">
                                    <button onClick={() => onDelete(e.id)} className="text-xs text-red-600 hover:underline" data-testid={`expense-delete-${e.id}`}>Delete</button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
}

function GstSummary({ gst }) {
    if (!gst) return <div className="p-6 text-sm text-zinc-500">Loading GST summary…</div>;
    return (
        <div className="space-y-4" data-testid="finance-gst-summary">
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} /> {gst.disclaimer}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">Output tax (sales)</div>
                    <div className="mt-2 font-mono text-2xl font-display tracking-tight">{inr(gst.totals.output)}</div>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">Input tax (purchases)</div>
                    <div className="mt-2 font-mono text-2xl font-display tracking-tight">{inr(gst.totals.input)}</div>
                </div>
                <div className="rounded-lg border border-zinc-950 bg-zinc-950 text-white p-4">
                    <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-400">Net payable</div>
                    <div className="mt-2 font-mono text-2xl font-display tracking-tight" data-testid="gst-net-payable">{inr(gst.totals.net_payable)}</div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <RateTable title="Output tax by rate" rows={gst.output_tax} />
                <RateTable title="Input tax by rate" rows={gst.input_tax} />
            </div>
            <div className="text-xs text-zinc-500">
                Period: {gst.period.from} → {gst.period.to}
            </div>
        </div>
    );
}

function RateTable({ title, rows }) {
    return (
        <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b border-zinc-100 text-sm font-medium">{title}</div>
            <Table>
                <TableHeader><TableRow className="bg-zinc-50/40">
                    <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Rate</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Taxable</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500 text-right">Tax</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow><TableCell colSpan={3} className="py-6 text-center text-xs text-zinc-500">No transactions</TableCell></TableRow>
                    ) : rows.map((r) => (
                        <TableRow key={r.rate}>
                            <TableCell className="font-mono">{r.rate}%</TableCell>
                            <TableCell className="text-right font-mono text-sm">{inr(r.taxable)}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{inr(r.tax)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
